import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type FieldValues,
  type Path,
  type UseFormReturn,
} from 'react-hook-form';
import {
  Button,
  ErrorText,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/shared/components';
import {
  useMfaIdentityOTP,
  type SendMfaOtpCodeRequestDto,
  type SendMfaOtpCodeResponseDto,
  type MFAIdentityType,
  MFA_IDENTITY_TYPES,
} from '@/entities/mfa-identity';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { useContactUs, useExecuteOnce, useTimer } from '@/shared/lib';
import {
  DEFAULT_OTP_CODE_LENGTH,
  DEFAULT_OTP_TIMEOUT_IN_MS,
} from '@/shared/config';
import { cn } from '@/shared/utils';

export type MfaConfirmationScreenProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  identityType: MFAIdentityType;
  identity?: string;
  state?: string;
  mfaCodeFieldName: Path<T>;
  sendOtpCodeMutationFn: (
    args: SendMfaOtpCodeRequestDto
  ) => Promise<SendMfaOtpCodeResponseDto>;
  setMaskedContact?: (contact: string) => void;
  setResendUntil?: (until: number) => void;
  onSubmit: () => void;
  isSubmitLoading: boolean;
  submitError: string | undefined;
  isInitialSetup?: boolean;
  shouldSendOtpOnMount?: boolean;
  expiresIn?: number;
  resendIn?: number;
  length?: number;
};

const MfaConfirmationScreen = <T extends FieldValues>({
  form,
  identityType,
  identity,
  state,
  mfaCodeFieldName,
  onSubmit,
  sendOtpCodeMutationFn,
  setMaskedContact,
  setResendUntil,
  submitError,
  expiresIn,
  resendIn,
  length,
  isInitialSetup = false,
  isSubmitLoading = false,
  shouldSendOtpOnMount = true,
}: MfaConfirmationScreenProps<T>) => {
  const { t } = useTranslation();
  const { timerValue, startTimer, isTimerRunning } = useTimer();
  const { links } = useContactUs();

  const isGoogleAuthenticator =
    identityType === MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR ||
    identityType === MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR_UPPERCASE;

  const { sendOtpCode, otpCodeError, otpCodeData } = useMfaIdentityOTP({
    mutationFn: sendOtpCodeMutationFn,
    onSuccess: data => {
      setMaskedContact?.(data.maskedContact);
      setResendUntil?.(Date.now() + data?.resendIn);
      startTimer(
        Math.floor(
          (data.resendIn || resendIn || DEFAULT_OTP_TIMEOUT_IN_MS) / 1000
        )
      );
    },
  });

  const handleSendOtpCode = useCallback(async () => {
    try {
      await sendOtpCode({
        identityType,
        identity,
        state: state || '',
      });
    } catch (error) {
      console.error('Failed to send OTP code:', error);
    }
  }, [sendOtpCode, identityType, state]);

  const onInputChange = useCallback(
    (value: string) => {
      const codeLength =
        otpCodeData?.length || length || DEFAULT_OTP_CODE_LENGTH;
      if (value.length === codeLength) {
        onSubmit();
      }
    },
    [otpCodeData, onSubmit]
  );

  useEffect(() => {
    if (resendIn && resendIn > 0 && !shouldSendOtpOnMount) {
      startTimer(Math.floor((resendIn || DEFAULT_OTP_TIMEOUT_IN_MS) / 1000));
    }
  }, [expiresIn, resendIn, shouldSendOtpOnMount]);

  useExecuteOnce(
    handleSendOtpCode,
    !isGoogleAuthenticator && shouldSendOtpOnMount
  );

  const otpLength = otpCodeData?.length || length || DEFAULT_OTP_CODE_LENGTH;

  const [showErrorHighlight, setShowErrorHighlight] = useState(false);
  useEffect(() => {
    if (submitError) setShowErrorHighlight(true);
  }, [submitError]);

  const showSlotError = !!submitError && showErrorHighlight;

  return (
    <Form {...form}>
      <form className="space-y-10" onSubmit={e => e.preventDefault()}>
        <FormField
          control={form.control}
          name={mfaCodeFieldName}
          render={({ field }) => (
            <FormItem className="mb-[1.4rem] pr-[1rem]">
              <FormControl>
                <InputOTP
                  // Adding key to force re-render of the component when isSubmitLoading changes
                  // to avoid the issue of the input not being focused when the error is shown
                  key={String(isSubmitLoading)}
                  autoFocus
                  disabled={isSubmitLoading}
                  maxLength={otpLength}
                  pattern={REGEXP_ONLY_DIGITS}
                  value={field.value as string}
                  onChange={(value: string) => {
                    field.onChange(value);
                    setShowErrorHighlight(false);
                    onInputChange(value);
                  }}>
                  <InputOTPGroup className="w-full">
                    {Array.from({ length: otpLength }).map((_, index) => (
                      <InputOTPSlot
                        className={cn(isSubmitLoading && 'animate-pulse')}
                        key={index}
                        index={index}
                        isError={showSlotError}
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              {form.getFieldState(mfaCodeFieldName).error ? (
                <FormMessage />
              ) : showSlotError ? (
                <ErrorText size="small">{submitError}</ErrorText>
              ) : null}
            </FormItem>
          )}
        />

        {!isGoogleAuthenticator && (
          <Button
            type="button"
            variant="link"
            onClick={handleSendOtpCode}
            disabled={isTimerRunning}
            className="headline5 p-0">
            {isTimerRunning
              ? `${t('authPages.passwordRecoveryConfirmationForm.resendCodePending')} ${timerValue}`
              : t(
                  'authPages.passwordRecoveryConfirmationForm.resendCodeButton'
                )}
          </Button>
        )}

        {isGoogleAuthenticator && !isInitialSetup && (
          <Button
            variant="link"
            asChild
            className="headline5 px-0 w-full h-[4.8rem] justify-start">
            <a href={links?.turnoffTwofaRequest || links?.defaultRequest || ''}>
              {t('authPages.identityConfirmationForm.lostAccess')}
            </a>
          </Button>
        )}

        {!showSlotError && otpCodeError && (
          <ErrorText size="small">
            {otpCodeError.message || t('common.unknownError')}
          </ErrorText>
        )}
      </form>
    </Form>
  );
};

export { MfaConfirmationScreen };
